// @ai-generated 2026-08-24T12:24:35+00:00 by kai.cissarek@synqony.com using Claude Opus 5
import { type ApiContainerAction } from '@Model';
import { type AsyncStateUpdateRequest, type Awaitable, FlexColumn } from '@synqo/frontend-component-library';
import { type ReactElement } from 'react';
import styles from './ContainerActions.module.css';
import { CollectiveContainerRow } from './CollectiveContainerRow';
import { type PlannedContainerGroup } from './containerActionGroups';
import { type ContainerOrderState } from './containerRowState';
import { ExchangeContainerRow } from './ExchangeContainerRow';
import { IndividualContainerRow } from './IndividualContainerRow';
import { type ExchangeContainerEntry, type PlannedContainerEntry } from './plannedContainerEntries';
import { type ContainerMaterial } from './useContainerMaterial';
import { type ContainerTypeChange } from './useContainerTypeChange';
import { type ContainerTypeDetailsByTypeId } from './useContainerTypeDetails';

interface PlannedContainerListProps {
    entries: PlannedContainerEntry[];
    /** Path to the order; each line appends its own segment. */
    baseApiObjectPath: string;
    canEdit: boolean;
    /** Whether types and materials may be linked; the link routes are guarded with Update. */
    canLink: boolean;
    canDelete: boolean;
    /** Whether the user may confirm lines and take confirmations back. */
    canMarkForBooking: boolean;
    isMarking: boolean;
    completing: boolean;
    focusActionId?: string;
    material: ContainerMaterial;
    typeChange: ContainerTypeChange;
    containerTypes: ContainerTypeDetailsByTypeId;
    orderState: ContainerOrderState;
    /** How far a collective group may grow, unset where nothing caps it. */
    capOf: (group: PlannedContainerGroup) => number | undefined;
    requestUpdateAction: (updater: AsyncStateUpdateRequest<ApiContainerAction>, actionId: string) => Awaitable<boolean>;
    onDelete: (actionIds: string[]) => void;
    /** Plans the given number of rows like the rows of a collective group; the group's plus is locked without it. */
    onGrowGroup?: (group: PlannedContainerGroup, count: number) => void;
    growLocked?: boolean;
    onMarkForBooking: (actionId: string) => void;
    onMarkGroupForBooking: (group: PlannedContainerGroup) => void;
    onMarkExchangeForBooking: (exchange: ExchangeContainerEntry) => void;
    onUnmarkForBooking: (actionId: string) => void;
    onUnmarkGroupForBooking: (group: PlannedContainerGroup) => void;
    onUnmarkExchangeForBooking: (exchange: ExchangeContainerEntry) => void;
}

/**
 * The planned containers, one line per collective type, per individually managed container and per exchange.
 * Renders nothing while nothing is planned: the card that holds it is closed in that state anyway.
 */
export function PlannedContainerList({
    entries,
    baseApiObjectPath,
    canEdit,
    canLink,
    canDelete,
    canMarkForBooking,
    isMarking,
    completing,
    focusActionId,
    material,
    typeChange,
    containerTypes,
    orderState,
    capOf,
    requestUpdateAction,
    onDelete,
    onGrowGroup,
    growLocked,
    onMarkForBooking,
    onMarkGroupForBooking,
    onMarkExchangeForBooking,
    onUnmarkForBooking,
    onUnmarkGroupForBooking,
    onUnmarkExchangeForBooking,
}: PlannedContainerListProps): ReactElement | undefined {
    if (entries.length === 0) {
        return undefined;
    }

    return (
        <FlexColumn gap="none" align="stretch" className={styles.lines}>
            {entries.map((entry) =>
                entry.kind === 'collective' ? (
                    <CollectiveContainerRow
                        key={entry.key}
                        group={entry.group}
                        baseApiObjectPath={baseApiObjectPath}
                        canSetMaterial={canLink}
                        canDelete={canDelete}
                        canMarkForBooking={canMarkForBooking}
                        isMarking={isMarking}
                        completing={completing}
                        material={material}
                        orderState={orderState}
                        standingCount={capOf(entry.group)}
                        requestUpdateAction={requestUpdateAction}
                        onDelete={onDelete}
                        onGrow={onGrowGroup}
                        growLocked={growLocked}
                        onMarkGroupForBooking={onMarkGroupForBooking}
                        typeChange={typeChange}
                        focused={entry.group.actions.some((action) => action.id === focusActionId)}
                        onUnmarkGroupForBooking={onUnmarkGroupForBooking}
                    />
                ) : entry.kind === 'exchange' ? (
                    <ExchangeContainerRow
                        key={entry.key}
                        exchange={entry}
                        pickupIndividual={isIndividual(containerTypes, entry.pickup)}
                        deliveryIndividual={isIndividual(containerTypes, entry.delivery)}
                        baseApiObjectPath={baseApiObjectPath}
                        canEdit={canEdit}
                        canSetLinks={canLink}
                        canDelete={canDelete}
                        canMarkForBooking={canMarkForBooking}
                        isMarking={isMarking}
                        completing={completing}
                        material={material}
                        orderState={orderState}
                        requestUpdateAction={requestUpdateAction}
                        onDelete={onDelete}
                        onMarkForBooking={onMarkExchangeForBooking}
                        onUnmarkForBooking={onUnmarkExchangeForBooking}
                        focused={focusActionId === entry.pickup.id || focusActionId === entry.delivery.id}
                    />
                ) : (
                    <IndividualContainerRow
                        key={entry.id}
                        action={entry.action}
                        baseApiObjectPath={baseApiObjectPath}
                        canEdit={canEdit}
                        canSetLinks={canLink}
                        canDelete={canDelete}
                        canMarkForBooking={canMarkForBooking}
                        isMarking={isMarking}
                        completing={completing}
                        material={material}
                        orderState={orderState}
                        requestUpdateState={(updater) => requestUpdateAction(updater, entry.action.id)}
                        onDelete={onDelete}
                        onMarkForBooking={onMarkForBooking}
                        onUnmarkForBooking={onUnmarkForBooking}
                        focused={entry.action.id === focusActionId}
                    />
                ),
            )}
        </FlexColumn>
    );
}

function isIndividual(containerTypes: ContainerTypeDetailsByTypeId, action: ApiContainerAction): boolean {
    return containerTypes.get(action.container_type.foreign_key ?? '')?.usesIndividualManagement === true;
}
